import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { adminService } from '../../../services/adminService';
import { extractList, extractMeta } from '../../../services/api';
import { parsePeriod, ROLES } from '../../../constants';
import Loading from '../../../shared/Loading';
import EmptyState from '../../../shared/EmptyState';
import Badge from '../../../shared/Badge';

const monthInputToPeriod = (value) => (value ? value.replace('-', '') : undefined);
const getCurrentMonthInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};
const MONTH_INPUT_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

const normalizeMonthInput = (value) => {
  const raw = String(value || '').trim();
  return MONTH_INPUT_REGEX.test(raw) ? raw : '';
};

const normalizeTextFilter = (value) => String(value || '').trim();

const normalizePage = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return fallback;
  return parsed;
};

const normalizeCompanyFilter = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return '';
  return String(parsed);
};

const resolvePeriodFilterFromSearchParams = (searchParams) => {
  if (!searchParams.has('period')) return getCurrentMonthInput();
  return normalizeMonthInput(searchParams.get('period'));
};

const COMPARISON_STATUS_LABELS = {
  changed: 'Changed',
  missing_in_current: 'Missing Current',
  added: 'Added',
  removed: 'Removed',
  same: 'Same',
  no_previous: 'No Previous',
  unknown: 'Unknown',
};

const getPeriodLabel = (period) => {
  if (!period) return '-';
  const { month, year, label } = parsePeriod(period);
  if (!Number.isFinite(month) || !Number.isFinite(year) || !label) return String(period);
  return label;
};

const normalizeCompanyId = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const isValidHttpUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const normalizeDownloadUrl = (value) => {
  if (!isValidHttpUrl(value)) return '';
  const parsed = new URL(value);
  if (window.location.protocol === 'https:' && parsed.protocol === 'http:') {
    parsed.protocol = 'https:';
  }
  return parsed.toString();
};

const triggerUrlDownload = (url) => {
  const popup = window.open(url, '_blank', 'noopener,noreferrer');
  if (popup) return;

  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getDisplayWilayah = (wilayah) => {
  if (typeof wilayah !== 'string') return '-';
  const trimmed = wilayah.trim();
  return trimmed || '-';
};

const getDisplayCompany = (reportItem, usersMap) => {
  return (
    reportItem?.company?.name
    || reportItem?.user?.company?.name
    || usersMap?.[reportItem?.user_id]?.company?.name
    || '-'
  );
};

const normalizeStatus = (status) => {
  const value = String(status || '').trim().toLowerCase();
  if (!value) return 'unknown';
  if (['changed', 'missing_in_current', 'added', 'removed', 'same'].includes(value)) return value;
  return 'unknown';
};

const resolveComparisonStatus = (items, previousDataAvailable) => {
  if (!items.length) return previousDataAvailable ? 'unknown' : 'no_previous';

  const statuses = items.map((item) => normalizeStatus(item.comparison_status));
  if (statuses.includes('missing_in_current')) return 'missing_in_current';
  if (statuses.includes('changed')) return 'changed';
  if (statuses.includes('added')) return 'added';
  if (statuses.includes('removed')) return 'removed';
  if (statuses.includes('same')) return 'same';
  return previousDataAvailable ? 'unknown' : 'no_previous';
};

const summarizeComparisonNotes = (items) => {
  const uniqueNotes = [...new Set(
    items
      .map((item) => (item?.comparison_notes || '').trim())
      .filter(Boolean)
  )];

  if (!uniqueNotes.length) return '-';
  if (uniqueNotes.length <= 2) return uniqueNotes.join(' | ');
  return `${uniqueNotes.slice(0, 2).join(' | ')} +${uniqueNotes.length - 2} lainnya`;
};

const buildComparisonSummary = (comparisonData) => {
  const items = Array.isArray(comparisonData?.items) ? comparisonData.items : [];
  const previousDataAvailable = Boolean(comparisonData?.previous_data_available);
  const status = resolveComparisonStatus(items, previousDataAvailable);

  return {
    status,
    label: COMPARISON_STATUS_LABELS[status] || COMPARISON_STATUS_LABELS.unknown,
    notes: summarizeComparisonNotes(items),
  };
};

const defaultComparisonSummary = () => ({
  status: 'unknown',
  label: COMPARISON_STATUS_LABELS.unknown,
  notes: '-',
});

const PERIOD_REGEX = /^\d{6}$/;

const buildEmployeeOptionLabel = (targetUser) => {
  const name = (targetUser?.name || '').trim() || `User #${targetUser?.user_id}`;
  const company = (targetUser?.company?.name || '').trim() || '-';
  const wilayah = getDisplayWilayah(targetUser?.wilayah);
  return `${name} - ${company} - ${wilayah}`;
};

const resolveCreateReportError = (error) => {
  if (error?.httpStatus === 409) {
    return 'Report untuk employee dan periode tersebut sudah ada.';
  }
  if (error?.httpStatus === 422) {
    return error?.message || 'Periode tidak valid atau periode masa depan tidak diizinkan.';
  }
  return error?.message || 'Gagal membuat report baru.';
};

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M9 3h6l1 2h4v2H4V5h4l1-2Zm-2 6h10l-1 11H8L7 9Zm3 2v7h2v-7h-2Zm4 0v7h2v-7h-2Z" />
  </svg>
);

const EmployeeSingleSelect = ({
  id,
  options,
  value,
  onChange,
  placeholder = 'Pilih employee...',
  disabled = false,
  className = '',
}) => {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const normalizedOptions = useMemo(() => (Array.isArray(options) ? options : []), [options]);
  const selectedOption = useMemo(
    () => normalizedOptions.find((option) => option.value === String(value)) || null,
    [normalizedOptions, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return normalizedOptions;
    return normalizedOptions.filter((option) => option.searchText.includes(normalizedQuery));
  }, [normalizedOptions, query]);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event) => {
      if (!rootRef.current || rootRef.current.contains(event.target)) return;
      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  const rootClassName = `dash-multiselect${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`;

  return (
    <div ref={rootRef} className={rootClassName}>
      <button
        id={id}
        type="button"
        className="dash-multiselect__trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className="dash-multiselect__summary">{selectedOption?.label || placeholder}</span>
        <span className="dash-multiselect__meta">{selectedOption ? 'Dipilih' : 'Pilih'}</span>
      </button>

      {open && !disabled && (
        <div className="dash-multiselect__panel">
          <div className="dash-multiselect__search-wrap">
            <input
              type="text"
              className="dash-multiselect__search"
              placeholder="Cari employee..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
              autoFocus
            />
          </div>

          <div className="dash-multiselect__options" role="listbox" aria-multiselectable="false">
            {!filteredOptions.length ? (
              <div className="dash-multiselect__empty">Employee tidak ditemukan</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.value === String(value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`dash-multiselect__option${isSelected ? ' is-selected' : ''}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option.value)}
                  >
                    <span>{option.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UniformReportsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isSuperadmin = user?.role === ROLES.SUPERADMIN;
  const loggedInCompanyId = normalizeCompanyId(user?.company_id ?? user?.company?.company_id);
  const [reports, setReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const [toast, setToast] = useState('');
  const toastTimerRef = useRef(null);
  const [usersMap, setUsersMap] = useState({});
  const [totalsMap, setTotalsMap] = useState({});
  const [comparisonMap, setComparisonMap] = useState({});
  const [isDownloadingPivotExcel, setIsDownloadingPivotExcel] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateSubmitting, setIsCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const selectAllCheckboxRef = useRef(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [createForm, setCreateForm] = useState({
    user_id: '',
    period: getCurrentMonthInput(),
    notes: '',
  });

  const showToast = (message) => {
    if (!message) return;
    setToast(message);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setToast('');
      toastTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => () => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
  }, []);

  const periodFilter = resolvePeriodFilterFromSearchParams(searchParams);
  const wilayahFilter = normalizeTextFilter(searchParams.get('wilayah'));
  const companyFilter = isSuperadmin ? normalizeCompanyFilter(searchParams.get('company_id')) : '';
  const searchFilter = normalizeTextFilter(searchParams.get('search'));
  const listPage = normalizePage(searchParams.get('page'));
  const currentPeriod = monthInputToPeriod(getCurrentMonthInput());
  const canDeleteReports = [ROLES.SUPERADMIN, ROLES.ADMIN_COMPANY].includes(user?.role)
    && monthInputToPeriod(periodFilter) === currentPeriod;
  const deletableReports = useMemo(() => (
    canDeleteReports
      ? reports.filter((reportItem) => String(reportItem.period) === currentPeriod)
      : []
  ), [canDeleteReports, currentPeriod, reports]);
  const selectedReportIdSet = useMemo(() => new Set(selectedReportIds), [selectedReportIds]);
  const allVisibleReportsSelected = deletableReports.length > 0
    && deletableReports.every((reportItem) => selectedReportIdSet.has(Number(reportItem.uniform_report_id)));

  useEffect(() => {
    adminService.getUsers({ limit: 1000 }).then((res) => {
      const map = {};
      extractList(res.data).forEach((u) => { map[u.user_id] = u; });
      setUsersMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isSuperadmin) return;
    adminService.getCompanies({ page: 1, limit: 1000 }).then((res) => {
      setCompanies(extractList(res.data));
    }).catch(() => {
      setCompanies([]);
    });
  }, [isSuperadmin]);

  useEffect(() => {
    setSelectedReportIds([]);
    setDeleteDialog(null);
    setDeleteError('');
  }, [listPage, periodFilter, wilayahFilter, companyFilter, searchFilter]);

  useEffect(() => {
    if (!selectAllCheckboxRef.current) return;
    const selectedVisibleCount = deletableReports.filter((reportItem) => (
      selectedReportIdSet.has(Number(reportItem.uniform_report_id))
    )).length;
    selectAllCheckboxRef.current.indeterminate = selectedVisibleCount > 0
      && selectedVisibleCount < deletableReports.length;
  }, [deletableReports, selectedReportIdSet]);

  const employeeOptions = useMemo(() => {
    const allUsers = Object.values(usersMap);
    const employeeUsers = allUsers.filter((targetUser) => targetUser?.role === ROLES.EMPLOYEE);
    const scopedUsers = isSuperadmin
      ? employeeUsers
      : (
        loggedInCompanyId === undefined
          ? []
          : employeeUsers.filter((targetUser) => {
            const targetCompanyId = normalizeCompanyId(targetUser?.company_id ?? targetUser?.company?.company_id);
            return targetCompanyId === loggedInCompanyId;
          })
      );

    return scopedUsers
      .filter((targetUser) => targetUser?.user_id)
      .map((targetUser) => {
        const label = buildEmployeeOptionLabel(targetUser);
        return {
          value: String(targetUser.user_id),
          label,
          searchText: label.toLowerCase(),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'id', { sensitivity: 'base' }));
  }, [isSuperadmin, loggedInCompanyId, usersMap]);

  const fetchReports = useCallback(async ({
    page = 1,
    monthValue = getCurrentMonthInput(),
    wilayahValue = '',
    companyValue = '',
    searchValue = '',
  } = {}) => {
    if (!hasLoadedOnceRef.current) setLoading(true);
    try {
      const res = await adminService.getReports({
        page,
        period: monthInputToPeriod(monthValue),
        wilayah: wilayahValue || undefined,
        company_id: isSuperadmin ? normalizeCompanyId(companyValue) : undefined,
        search: normalizeTextFilter(searchValue) || undefined,
      });
      const list = extractList(res.data);
      setReports(list);
      setPagination(extractMeta(res.data));

      if (!list.length) {
        setTotalsMap({});
        setComparisonMap({});
        return;
      }

      const perReportResults = await Promise.all(
        list.map(async (reportItem) => {
          const detailPromise = adminService.getReportDetails(reportItem.uniform_report_id, { page: 1, limit: 100 })
            .then((detailRes) => {
              const items = extractList(detailRes.data);
              return items.reduce((sum, detail) => sum + (Number(detail.qty) || 0), 0);
            })
            .catch(() => Number(reportItem.total_uniform_received) || 0);

          const comparisonPromise = reportItem.user_id && reportItem.period
            ? adminService.getUniformComparisonByUser(reportItem.user_id, { period: reportItem.period })
              .then((compareRes) => buildComparisonSummary(compareRes?.data))
              .catch(() => defaultComparisonSummary())
            : Promise.resolve(defaultComparisonSummary());

          const [total, comparisonSummary] = await Promise.all([detailPromise, comparisonPromise]);
          return { id: reportItem.uniform_report_id, total, comparisonSummary };
        })
      );

      const totalMap = {};
      const compareMap = {};
      perReportResults.forEach(({ id, total, comparisonSummary }) => {
        totalMap[id] = total;
        compareMap[id] = comparisonSummary;
      });
      setTotalsMap(totalMap);
      setComparisonMap(compareMap);
    } catch {
      setReports([]);
      setTotalsMap({});
      setComparisonMap({});
    } finally {
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, [isSuperadmin]);

  const openCreateModal = () => {
    setCreateError('');
    setCreateForm({
      user_id: '',
      period: periodFilter || getCurrentMonthInput(),
      notes: '',
    });
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (isCreateSubmitting) return;
    setIsCreateModalOpen(false);
    setCreateError('');
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    setCreateError('');

    const userId = Number(createForm.user_id);
    if (!Number.isInteger(userId) || userId < 1) {
      setCreateError('Employee wajib dipilih dari daftar.');
      return;
    }

    const normalizedPeriodInput = normalizeMonthInput(createForm.period);
    const period = monthInputToPeriod(normalizedPeriodInput);
    if (!period || !PERIOD_REGEX.test(period)) {
      setCreateError('Periode wajib diisi dengan format YYYYMM.');
      return;
    }

    const currentPeriod = monthInputToPeriod(getCurrentMonthInput());
    if (currentPeriod && period > currentPeriod) {
      setCreateError('Periode masa depan tidak diizinkan.');
      return;
    }

    const payload = {
      user_id: userId,
      period,
    };

    if (createForm.notes.trim()) {
      payload.notes = createForm.notes.trim();
    }

    setIsCreateSubmitting(true);
    try {
      const response = await adminService.createReport(payload);
      showToast(response?.message || 'Laporan berhasil dibuat');
      setIsCreateModalOpen(false);
      await fetchReports({
        page: listPage,
        monthValue: periodFilter,
        wilayahValue: wilayahFilter,
        companyValue: companyFilter,
        searchValue: searchFilter,
      });
    } catch (error) {
      setCreateError(resolveCreateReportError(error));
    } finally {
      setIsCreateSubmitting(false);
    }
  };

  useEffect(() => {
    fetchReports({
      page: listPage,
      monthValue: periodFilter,
      wilayahValue: wilayahFilter,
      companyValue: companyFilter,
      searchValue: searchFilter,
    });
  }, [fetchReports, listPage, periodFilter, wilayahFilter, companyFilter, searchFilter]);

  const updateListSearchParams = ({
    periodValue = periodFilter,
    wilayahValue = wilayahFilter,
    companyValue = companyFilter,
    searchValue = searchFilter,
    pageValue = listPage,
  } = {}) => {
    const next = new URLSearchParams(searchParams);

    if (periodValue === '') {
      next.set('period', '');
    } else {
      const normalizedPeriod = normalizeMonthInput(periodValue);
      if (normalizedPeriod) next.set('period', normalizedPeriod);
      else next.delete('period');
    }

    const normalizedWilayah = normalizeTextFilter(wilayahValue);
    if (normalizedWilayah) next.set('wilayah', normalizedWilayah);
    else next.delete('wilayah');

    if (isSuperadmin) {
      const normalizedCompany = normalizeCompanyFilter(companyValue);
      if (normalizedCompany) next.set('company_id', normalizedCompany);
      else next.delete('company_id');
    } else {
      next.delete('company_id');
    }

    const normalizedSearch = normalizeTextFilter(searchValue);
    if (normalizedSearch) next.set('search', normalizedSearch);
    else next.delete('search');

    const normalizedPage = normalizePage(pageValue);
    if (normalizedPage > 1) next.set('page', String(normalizedPage));
    else next.delete('page');

    setSearchParams(next);
  };

  const handlePeriodChange = (event) => {
    const value = event.target.value;
    updateListSearchParams({
      periodValue: value,
      pageValue: 1,
    });
  };

  const handleWilayahChange = (event) => {
    const value = event.target.value;
    updateListSearchParams({
      wilayahValue: value,
      pageValue: 1,
    });
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value;
    updateListSearchParams({
      companyValue: value,
      pageValue: 1,
    });
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    updateListSearchParams({
      searchValue: value,
      pageValue: 1,
    });
  };

  const handleResetFilters = () => {
    updateListSearchParams({
      periodValue: '',
      wilayahValue: '',
      companyValue: '',
      searchValue: '',
      pageValue: 1,
    });
  };

  const toggleReportSelection = (reportId) => {
    const normalizedId = Number(reportId);
    setSelectedReportIds((previousIds) => (
      previousIds.includes(normalizedId)
        ? previousIds.filter((id) => id !== normalizedId)
        : [...previousIds, normalizedId]
    ));
  };

  const toggleSelectAllReports = () => {
    const visibleIds = deletableReports.map((reportItem) => Number(reportItem.uniform_report_id));
    setSelectedReportIds(allVisibleReportsSelected ? [] : visibleIds);
  };

  const openSingleDeleteDialog = (reportItem) => {
    setDeleteError('');
    setDeleteDialog({
      mode: 'single',
      reportIds: [Number(reportItem.uniform_report_id)],
      report: reportItem,
    });
  };

  const openBulkDeleteDialog = () => {
    if (!selectedReportIds.length) return;
    setDeleteError('');
    setDeleteDialog({
      mode: 'bulk',
      reportIds: selectedReportIds,
      report: null,
    });
  };

  const closeDeleteDialog = () => {
    if (isDeleteSubmitting) return;
    setDeleteDialog(null);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog?.reportIds?.length || isDeleteSubmitting) return;

    setIsDeleteSubmitting(true);
    setDeleteError('');

    try {
      const response = deleteDialog.mode === 'single'
        ? await adminService.deleteReport(deleteDialog.reportIds[0])
        : await adminService.bulkDeleteReports(deleteDialog.reportIds);
      const deletedIds = deleteDialog.reportIds;
      const deletedOnCurrentPage = deletedIds.filter((id) => (
        reports.some((reportItem) => Number(reportItem.uniform_report_id) === Number(id))
      )).length;

      setDeleteDialog(null);
      setSelectedReportIds([]);
      showToast(response?.message || 'Laporan berhasil dihapus');

      if (reports.length > 0 && deletedOnCurrentPage >= reports.length && listPage > 1) {
        updateListSearchParams({ pageValue: listPage - 1 });
      } else {
        await fetchReports({
          page: listPage,
          monthValue: periodFilter,
          wilayahValue: wilayahFilter,
          companyValue: companyFilter,
          searchValue: searchFilter,
        });
      }
    } catch (error) {
      setDeleteError(error?.message || 'Laporan gagal dihapus. Silakan coba lagi.');
    } finally {
      setIsDeleteSubmitting(false);
    }
  };

  const handleDownloadPivotExcel = async () => {
    if (isDownloadingPivotExcel) return;

    const period = monthInputToPeriod(periodFilter);
    if (!period) {
      window.alert('Periode harus diisi (format YYYYMM)');
      return;
    }
    const companyId = isSuperadmin ? normalizeCompanyId(companyFilter) : undefined;
    if (isSuperadmin && companyId === undefined) {
      window.alert('Company harus dipilih untuk download Pivot Excel');
      return;
    }

    setIsDownloadingPivotExcel(true);
    try {
      const response = await adminService.getUniformPivotExcelUrl({
        period,
        company_id: companyId,
      });
      const fileUrl = normalizeDownloadUrl(response?.data);

      if (!isValidHttpUrl(fileUrl)) {
        throw new Error('URL file download tidak valid');
      }

      if (window.location.protocol === 'https:' && new URL(fileUrl).protocol !== 'https:') {
        throw new Error('URL download harus HTTPS saat aplikasi diakses melalui HTTPS.');
      }

      triggerUrlDownload(fileUrl);
    } catch (error) {
      window.alert(
        error?.message
        || 'Gagal mengunduh Pivot Excel. Jika muncul Mixed Content, minta backend/devops memastikan seluruh redirect file download menggunakan HTTPS.'
      );
    } finally {
      setIsDownloadingPivotExcel(false);
    }
  };

  const selectedPeriodLabel = periodFilter
    ? getPeriodLabel(monthInputToPeriod(periodFilter))
    : '';
  const selectedCompanyLabel = companyFilter
    ? (companies.find((item) => String(item.company_id) === String(companyFilter))?.name || companyFilter)
    : '';
  const wilayahOptions = [...new Set(
    Object.values(usersMap)
      .map((item) => (typeof item?.wilayah === 'string' ? item.wilayah.trim() : ''))
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'id', { sensitivity: 'base' }));
  const selectedFilters = [
    selectedPeriodLabel ? `Periode ${selectedPeriodLabel}` : '',
    wilayahFilter ? `Wilayah ${wilayahFilter}` : '',
    isSuperadmin && selectedCompanyLabel ? `Company ${selectedCompanyLabel}` : '',
    searchFilter ? `Search "${searchFilter}"` : '',
  ].filter(Boolean);
  const returnToListPath = `/admin/uniform-reports${location.search || ''}`;

  if (loading && !hasLoadedOnceRef.current) return <Loading />;

  return (
    <div>
      {toast && <div className="toast">{toast}</div>}

      <div className="admin-users-toolbar">
        <div className="admin-table-controls">
          <label htmlFor="reports-period-filter">Periode</label>
          <input
            id="reports-period-filter"
            type="month"
            className="admin-filter-select"
            value={periodFilter}
            onChange={handlePeriodChange}
          />

          <label htmlFor="reports-wilayah-filter">Wilayah</label>
          <select
            id="reports-wilayah-filter"
            className="admin-filter-select"
            value={wilayahFilter}
            onChange={handleWilayahChange}
          >
            <option value="">Semua Wilayah</option>
            {wilayahOptions.map((wilayah) => (
              <option key={wilayah} value={wilayah}>{wilayah}</option>
            ))}
          </select>

          <label htmlFor="reports-search-filter">Search</label>
          <input
            id="reports-search-filter"
            type="text"
            className="admin-filter-select"
            value={searchFilter}
            onChange={handleSearchChange}
            placeholder="Cari nama/email employee"
          />

          {isSuperadmin && (
            <>
              <label htmlFor="reports-company-filter">Company</label>
              <select
                id="reports-company-filter"
                className="admin-filter-select"
                value={companyFilter}
                onChange={handleCompanyChange}
              >
                <option value="">Semua Company</option>
                {companies.map((company) => (
                  <option key={company.company_id} value={company.company_id}>{company.name}</option>
                ))}
              </select>
            </>
          )}

          {(periodFilter || wilayahFilter || companyFilter) && (
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={handleResetFilters}
            >
              Reset Filter
            </button>
          )}

          <button
            type="button"
            className="admin-btn-primary"
            onClick={openCreateModal}
          >
            Create New Report
          </button>

          <button
            type="button"
            className="admin-btn-primary"
            onClick={handleDownloadPivotExcel}
            disabled={isDownloadingPivotExcel}
          >
            {isDownloadingPivotExcel ? 'Menyiapkan file...' : 'Download Pivot Excel'}
          </button>
        </div>
      </div>

      <p className="admin-page__meta">
        Total: {pagination.total} laporan{selectedFilters.length ? ` (${selectedFilters.join(', ')})` : ''}
      </p>

      {canDeleteReports && selectedReportIds.length > 0 && (
        <div className="admin-report-bulk-actions">
          <span>{selectedReportIds.length} laporan dipilih</span>
          <button
            type="button"
            className="admin-report-delete-button"
            onClick={openBulkDeleteDialog}
            aria-label={`Hapus ${selectedReportIds.length} laporan terpilih`}
            title="Hapus laporan terpilih"
          >
            <TrashIcon />
            <span>Hapus</span>
          </button>
        </div>
      )}

      {reports.length === 0 ? (
        <EmptyState icon={'\uD83D\uDCCB'} title="Tidak ada laporan" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                {canDeleteReports && (
                  <th className="admin-report-select-cell">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      className="admin-report-checkbox"
                      checked={allVisibleReportsSelected}
                      onChange={toggleSelectAllReports}
                      disabled={!deletableReports.length}
                      aria-label="Pilih semua laporan pada halaman ini"
                    />
                  </th>
                )}
                <th>Periode</th>
                <th>Employee</th>
                <th>Company</th>
                <th>Wilayah</th>
                <th>Total Pcs</th>
                <th>Status Report</th>
                <th>Status Perbandingan</th>
                <th>Catatan Perbandingan</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((reportItem) => {
                const comparisonSummary = comparisonMap[reportItem.uniform_report_id] || defaultComparisonSummary();
                const reportId = Number(reportItem.uniform_report_id);
                const canDeleteReport = canDeleteReports && String(reportItem.period) === currentPeriod;

                return (
                  <tr key={reportItem.uniform_report_id}>
                    {canDeleteReports && (
                      <td className="admin-report-select-cell">
                        {canDeleteReport && (
                          <input
                            type="checkbox"
                            className="admin-report-checkbox"
                            checked={selectedReportIdSet.has(reportId)}
                            onChange={() => toggleReportSelection(reportId)}
                            aria-label={`Pilih laporan ${reportItem.user?.name || usersMap[reportItem.user_id]?.name || reportId}`}
                          />
                        )}
                      </td>
                    )}
                    <td>{getPeriodLabel(reportItem.period)}</td>
                    <td>{reportItem.user?.name || usersMap[reportItem.user_id]?.name || '-'}</td>
                    <td>{getDisplayCompany(reportItem, usersMap)}</td>
                    <td>{getDisplayWilayah(reportItem.user?.wilayah || usersMap[reportItem.user_id]?.wilayah)}</td>
                    <td>{totalsMap[reportItem.uniform_report_id] ?? reportItem.total_uniform_received}</td>
                    <td><Badge status={reportItem.report_status} /></td>
                    <td>
                      <span className={`admin-compare-pill admin-compare-pill--${comparisonSummary.status}`}>
                        {comparisonSummary.label}
                      </span>
                    </td>
                    <td
                      className="admin-compare-notes-summary"
                      title={comparisonSummary.notes}
                    >
                      {comparisonSummary.notes}
                    </td>
                    <td>
                      <div className="admin-user-actions">
                        <button
                          type="button"
                          className="admin-btn-link"
                          onClick={() => navigate(`/admin/uniform-reports/${reportItem.uniform_report_id}`)}
                        >
                          Detail {'->'}
                        </button>
                        <button
                          type="button"
                          className="admin-user-action"
                          disabled={!reportItem.user_id || !reportItem.period}
                          onClick={() => {
                            const params = new URLSearchParams();
                            params.set('user_id', String(reportItem.user_id));
                            params.set('period', String(reportItem.period));
                            params.set('report_id', String(reportItem.uniform_report_id));
                            params.set('return_to', returnToListPath);
                            navigate(`/admin/uniform-reports/compare?${params.toString()}`);
                          }}
                        >
                          Bandingkan
                        </button>
                        {canDeleteReport && (
                          <button
                            type="button"
                            className="admin-user-action admin-user-action--danger admin-report-delete-icon"
                            onClick={() => openSingleDeleteDialog(reportItem)}
                            aria-label="Hapus laporan"
                            title="Hapus laporan"
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isCreateModalOpen && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card" style={{ maxWidth: '620px' }}>
            <div className="admin-modal-header">
              <h3>Create New Report</h3>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeCreateModal}
                disabled={isCreateSubmitting}
              >
                x
              </button>
            </div>

            <form className="admin-modal-form" onSubmit={handleCreateSubmit}>
              <div className="admin-modal-grid">
                <div className="admin-modal-field admin-modal-field--full">
                  <label htmlFor="create-report-user">Employee *</label>
                  <EmployeeSingleSelect
                    id="create-report-user"
                    options={employeeOptions}
                    value={createForm.user_id}
                    onChange={(value) => setCreateForm((prev) => ({ ...prev, user_id: value }))}
                    placeholder={employeeOptions.length ? 'Pilih employee...' : 'Tidak ada employee tersedia'}
                    disabled={isCreateSubmitting || !employeeOptions.length}
                    className="admin-report-create-user-select"
                  />
                </div>

                <div className="admin-modal-field">
                  <label htmlFor="create-report-period">Periode *</label>
                  <input
                    id="create-report-period"
                    type="month"
                    value={createForm.period}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, period: event.target.value }))}
                    required
                    disabled={isCreateSubmitting}
                  />
                </div>

                <div className="admin-modal-field">
                  <label htmlFor="create-report-period-raw">Format YYYYMM</label>
                  <input
                    id="create-report-period-raw"
                    type="text"
                    value={monthInputToPeriod(normalizeMonthInput(createForm.period)) || '-'}
                    disabled
                  />
                </div>

                <div className="admin-modal-field admin-modal-field--full">
                  <label htmlFor="create-report-notes">Notes</label>
                  <input
                    id="create-report-notes"
                    type="text"
                    value={createForm.notes}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, notes: event.target.value }))}
                    placeholder="Opsional"
                    disabled={isCreateSubmitting}
                  />
                </div>
              </div>

              {createError && (
                <p className="admin-help-text" style={{ color: '#b91c1c', marginTop: '12px' }}>
                  {createError}
                </p>
              )}

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={closeCreateModal}
                  disabled={isCreateSubmitting}
                >
                  Batal
                </button>
                <button type="submit" className="admin-btn-primary" disabled={isCreateSubmitting}>
                  {isCreateSubmitting ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteDialog && (
        <div className="admin-modal-overlay" onClick={closeDeleteDialog}>
          <div
            className="admin-modal-card admin-report-delete-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-report-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-modal-header">
              <h3 id="delete-report-dialog-title">Konfirmasi Hapus Laporan</h3>
              <button
                type="button"
                className="admin-modal-close"
                onClick={closeDeleteDialog}
                disabled={isDeleteSubmitting}
                aria-label="Tutup dialog"
              >
                x
              </button>
            </div>
            <div className="admin-modal-form">
              {deleteDialog.mode === 'single' ? (
                <p className="admin-help-text">
                  Hapus laporan <strong>{deleteDialog.report?.user?.name || usersMap[deleteDialog.report?.user_id]?.name || '-'}</strong>{' '}
                  untuk periode <strong>{getPeriodLabel(deleteDialog.report?.period)}</strong>? Data yang sudah dihapus tidak dapat dipulihkan.
                </p>
              ) : (
                <p className="admin-help-text">
                  Hapus <strong>{deleteDialog.reportIds.length} laporan</strong> yang dipilih? Data yang sudah dihapus tidak dapat dipulihkan.
                </p>
              )}

              {deleteError && <p className="admin-report-delete-error">{deleteError}</p>}

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={closeDeleteDialog}
                  disabled={isDeleteSubmitting}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="admin-btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={isDeleteSubmitting}
                >
                  {isDeleteSubmitting ? 'Menghapus...' : 'Hapus'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pagination.last_page > 1 && (
        <div className="admin-pagination">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => updateListSearchParams({ pageValue: pagination.page - 1 })}
          >
            {'< Prev'}
          </button>
          <span>Hal {pagination.page} / {pagination.last_page}</span>
          <button
            type="button"
            disabled={pagination.page >= pagination.last_page}
            onClick={() => updateListSearchParams({ pageValue: pagination.page + 1 })}
          >
            {'Next >'}
          </button>
        </div>
      )}

      {isDownloadingPivotExcel && (
        <div className="admin-modal-overlay" aria-live="polite" aria-busy="true">
          <div className="admin-modal-card" style={{ maxWidth: '440px' }}>
            <Loading text="Menyiapkan Pivot Excel..." />
          </div>
        </div>
      )}
    </div>
  );
};

export default UniformReportsPage;
